import {
  ANCHOR_PATTERNS_BY_LABEL,
  type AnchorPattern,
  type NodeLabel
} from '@atbash-labs/ontology-core'
import type { PlantEntityConnectionGroup } from '../types'

/**
 * Build a Cypher AND-chain that is true when `alias` matches none of the
 * one-hop anchor patterns (i.e. the node is a stray). Empty pattern lists
 * return null — the label is stray-exempt.
 */
export function strayPredicate(
  alias: string,
  patterns: ReadonlyArray<AnchorPattern>
): string | null {
  if (patterns.length === 0) return null
  const clauses: string[] = []
  for (const pattern of patterns) {
    for (const peer of pattern.peerLabels) {
      if (pattern.direction === 'in') {
        clauses.push(`NOT (:\`${peer}\`)-[:\`${pattern.type}\`]->(${alias})`)
      } else {
        clauses.push(`NOT (${alias})-[:\`${pattern.type}\`]->(:\`${peer}\`)`)
      }
    }
  }
  return clauses.join('\n        AND ')
}

/**
 * Standalone registry / dump / live-SQL catalogs are first-class
 * Compute hosts (`urn:db:<name>`, `source_kind` tabular_dump|sql).
 * They are not hung off `:Compute` via `HAS_DATABASE`, so the generated
 * Database anchor would otherwise mark them as Model strays.
 */
export function databaseSelfHostedCypher(alias: string): string {
  return `(
          ${alias}.source_kind = 'tabular_dump'
          OR ${alias}.source_kind = 'sql'
          OR toUpper(coalesce(${alias}.database_type, '')) = 'DUCKDB'
          OR toLower(coalesce(${alias}.url, '')) STARTS WITH 'duckdb:'
          OR ${alias}.id STARTS WITH 'urn:db:'
        )`
}

export function strayPredicateForLabel(alias: string, label: NodeLabel | string): string | null {
  const predicate = strayPredicate(alias, anchorPatternsFor(label))
  if (predicate === null) return null
  if (label === 'Database') {
    return `${predicate}\n        AND NOT ${databaseSelfHostedCypher(alias)}`
  }
  return predicate
}

/** Anchor patterns for `label`, or [] when stray-exempt. */
export function anchorPatternsFor(label: NodeLabel | string): ReadonlyArray<AnchorPattern> {
  if (!Object.hasOwn(ANCHOR_PATTERNS_BY_LABEL, label)) return []
  return ANCHOR_PATTERNS_BY_LABEL[label as NodeLabel]
}

/**
 * Cypher boolean expression: true when `alias` is a stray for `label`.
 * Exempt labels compile to `false`.
 */
export function isStrayCypherExpr(alias: string, label: NodeLabel | string): string {
  const predicate = strayPredicateForLabel(alias, label)
  if (predicate === null) return 'false'
  return `(\n          ${predicate}\n        )`
}

/**
 * Cypher list expression: subset of `labelsExpr` for which `alias` is a
 * stray. Used by entity resolve / multi-kind detail queries.
 */
export function strayLabelsCypherExpr(alias: string, labelsExpr: string): string {
  const whens: string[] = []
  for (const label of Object.keys(ANCHOR_PATTERNS_BY_LABEL).sort() as NodeLabel[]) {
    const predicate = strayPredicateForLabel(alias, label)
    if (predicate === null) continue
    whens.push(`WHEN '${label}' THEN (${predicate})`)
  }
  if (whens.length === 0) return '[]'
  return `[lbl IN ${labelsExpr} WHERE CASE lbl ${whens.join(' ')} ELSE false END]`
}

/**
 * Client-side mirror of `strayPredicate`: true when the entity has no
 * one-hop edge matching any anchor pattern for `label`. Exempt labels
 * (empty pattern lists) are never strays.
 */
export function isPlantEntityStray(
  label: NodeLabel | string,
  groups: ReadonlyArray<PlantEntityConnectionGroup>
): boolean {
  const patterns = anchorPatternsFor(label)
  if (patterns.length === 0) return false
  return !patterns.some((pattern) =>
    pattern.peerLabels.some((peer) =>
      groups.some(
        (group) =>
          group.type === pattern.type &&
          group.direction === pattern.direction &&
          group.rows.some((row) => row.peerLabels.includes(peer))
      )
    )
  )
}
